import {
	ConfirmationDialog,
	ContextMenu,
	ContextMenuItem,
	IconButton,
	Id,
	Loading,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { ContactFilterApplyDrawer, ContactFilterBar } from "../contactFilter";
import { ContactExportButton } from "../contacts/ContactExportButton";
import { useGlobalContactView } from "../contactViews/global/GlobalContactViewProvider";
import { birthdayWidgetService } from "../dashboard/widgets/birthdays/service";
import { companyService } from "../shared/api/companyService";
import { contactService } from "../shared/api/contactService";
import { TenantArchiveDrawer } from "../shared/tenantArchive/TenantArchiveDrawer";
import { useRestoreByTenantHandler } from "../shared/tenantArchive/useRestoreByTenantHandler";
import { IContactIndexResource } from "../shared/types";
import { useContactListUtils } from "./useContactListUtils";

export const ArchivedContactList: FC = () => {
	const { t } = useTranslation();
	const { globalContactView, isLoading: contactViewLoading } = useGlobalContactView();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { columns: baseColumns } = useContactListUtils(globalContactView);
	const applyFilterDrawerProps = useEditDrawer();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeContactFilterId = useMemo(() => {
		const id = searchParams.get("contactFilterId");
		return id ? +id : null;
	}, [searchParams]);
	const queryKey = useMemo(() => {
		if (globalContactView?.id) {
			return [...contactService.keys.listArchived, String(activeContactFilterId), String(globalContactView?.id)];
		}
		return [...contactService.keys.listArchived, String(activeContactFilterId)];
	}, [activeContactFilterId, globalContactView?.id]);

	const initialSortingAttribute =
		globalContactView?.contactAttributes.find((attribute) => attribute.entityType === "lastName") ??
		globalContactView?.contactAttributes.find((attribute) => attribute.entityType === "createdAt");
	const {
		clickHandler: restoreClickHandler,
		drawerProps,
		confirmationProps,
	} = useRestoreByTenantHandler(contactService, () => {
		void queryClient.invalidateQueries({ queryKey: companyService.indexKey });
		void queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key });
	});
	const onApplyContactFilter = useCallback(
		(id: Id | null) => {
			if (id) {
				setSearchParams((prev) => {
					prev.set("contactFilterId", `${id}`);
					return prev;
				});
			} else {
				setSearchParams((prev) => {
					prev.delete("contactFilterId");
					return prev;
				});
			}
			void queryClient.invalidateQueries({ queryKey: contactService.listKey });
		},
		[setSearchParams, queryClient],
	);

	const openContactFilterDrawer = useCallback(() => {
		applyFilterDrawerProps.handleOpen(activeContactFilterId || "new");
	}, [activeContactFilterId, applyFilterDrawerProps]);

	if (contactViewLoading) {
		return <Loading />;
	}

	return (
		<>
			<PaginatedAutoDataList
				name="contacts"
				empty={t("No contact available.")}
				headerChildren={
					<ContactFilterBar
						contactFilterId={activeContactFilterId}
						onApply={onApplyContactFilter}
						onClick={openContactFilterDrawer}
					/>
				}
				heading={t("Contacts")}
				columns={[
					...baseColumns,
					{
						heading: "",
						type: "actions",
						size: "max-content",
						action: ({ id }) => (
							<ContextMenu displayShortcutsAs="icon">
								<ContextMenuItem
									title={t("Restore")}
									label={t("Restore")}
									action={() => restoreClickHandler(id)}
									icon="unarchive"
									shortcut="primary"
								/>
							</ContextMenu>
						),
					},
				]}
				listFetchConfig={laravelPaginatedListFetchConfig(
					queryKey,
					(config: PaginatedListFetcherConfig<IContactIndexResource>) =>
						contactService.indexArchivedWithContactFilter(config, activeContactFilterId, globalContactView?.id ?? null),
				)}
				headerActions={
					<>
						<IconButton size="small" title={t("Contact filter")} icon="filter" onClick={openContactFilterDrawer} />
						<ContactExportButton isArchived={true} filterId={activeContactFilterId} viewId={globalContactView?.id} />
					</>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
						{ name: "phone", label: t("Phone") },
						{ name: "company", label: t("Company") },
					],
				}}
				onRowClick={({ id }) => navigate(`${id}`)}
				initialSorting={
					initialSortingAttribute ? { column: initialSortingAttribute.id as any, direction: "asc" } : null
				}
			/>
			{applyFilterDrawerProps.editEntityId != null && (
				<ContactFilterApplyDrawer
					{...applyFilterDrawerProps}
					contactFilterId={activeContactFilterId}
					onApply={onApplyContactFilter}
				/>
			)}
			<TenantArchiveDrawer {...drawerProps} title={t("Restore")} type="restore" service={contactService} />
			<ConfirmationDialog
				{...confirmationProps}
				heading={t("Restore")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
			>
				{t("Are you sure you want to restore this contact?")}
			</ConfirmationDialog>
		</>
	);
};
